import React, { useState, useMemo, useRef } from 'react';
import cn from 'classnames';
import { noop } from 'lodash';
import { URI } from '@fiverr-private/futile';
import pathfinder from '@fiverr-private/pathfinder';
import Variation from '../../base/Variation';
import { setVariationTextFields, hasCurvedText } from '../../../lib';
import { LOGO_PROJECT_CARD, DEFAULT_TARGET_TYPE, TARGET_TYPES } from '../../../propTypes';
import Slider from '../../Slider';
import DotsIndicator from '../../Indicator';
import CardSellerProfile from '../CardSellerProfile';
import VariationsPreviewCard from '../VariationsPreviewCard';
import {
    FAVORITE_PROJECT_CARD_CLASSNAME,
    PROJECT_CARD_DOM_ID_ATTRIBUTE,
    CONTAINER_PADDINGS,
    MAX_PREVIEW_WIDTH,
    PREVIEW_PADDING,
} from './constants';
import FavoriteButton from './FavoriteButton';
import Footer from './Footer';
import './index.scss';

const LogoProjectCard = ({
    className,
    variations = [],
    isMobile = false,
    isTablet = false,
    id,
    activeVariationIndex: initialActiveVariationIndex = 0,
    brandName = '',
    slogan = '',
    userSaved = null,
    showUserSaved = false,
    onUserSavingChange = noop,
    onPreviewLoad = noop,
    navigationParams,
    onLogoProjectCardClick = noop,
    onLogoProjectArrowClick = noop,
    onLogoProjectHover = noop,
    onLogoProjectVariationHover = noop,
    onLoadMoreVariationsClick = noop,
    logoStyles: taggedLogoStyles = [],
    target = DEFAULT_TARGET_TYPE,
    seller = {},
    withMetadataCard = true,
    withArrowSlider = false,
    setTriggerRef,
    skipChooseVariation = false,
    isLoading = false,
}) => {
    const containerRef = useRef(null);
    const containerWidth = containerRef.current && containerRef.current.clientWidth;

    const [activeVariationIndex, setActiveVariationIndex] = useState(initialActiveVariationIndex);
    const [displayActiveVariationIndex, setDisplayActiveVariationIndex] = useState(activeVariationIndex);
    const [showPreviews, setShowPreviews] = useState(false);
    const [direction, setDirection] = useState(null);

    const activeVariationId = variations[activeVariationIndex].id;

    const maxPreviewsCounter =
        (containerWidth &&
            Math.floor(
                (containerWidth - CONTAINER_PADDINGS) /
                    ((isTablet ? MAX_PREVIEW_WIDTH.TABLET : MAX_PREVIEW_WIDTH.DESKTOP) + PREVIEW_PADDING)
            ) - 1) ||
        0;
    const leftoverVariationsCounter =
        variations.length > maxPreviewsCounter ? variations.length - maxPreviewsCounter + 1 : 0;

    /* eslint-disable arrow-body-style */
    const chooseVariationsURL = useMemo(() => {
        return new URI(pathfinder('logo_maker_choose_project_variation_page', { project_id: id }))
            .addParams({ variation_id: activeVariationId, brand_name: brandName, slogan, ...navigationParams })
            .toString();
    }, [id, brandName, slogan, activeVariationId, navigationParams]);

    /* eslint-disable arrow-body-style */
    const editorURL = useMemo(() => {
        return new URI(
            pathfinder('logo_maker_buyer_editor_page', {
                project_id: id,
                variation_id: activeVariationId,
            })
        )
            .addParams({ ...navigationParams, brand_name: brandName, slogan })
            .toString();
    }, [id, activeVariationId, navigationParams, brandName, slogan]);

    const variationsUrl = skipChooseVariation ? editorURL : chooseVariationsURL;

    const displayVariations = variations.map((variation) => ({
        ...setVariationTextFields({ variation, brandName, slogan }),
        applyVerticalChanges: !hasCurvedText(variation),
        withWatermark: true,
        onPreviewLoad,
    }));
    const previewVariations = displayVariations
        .map((variation) => ({
            ...variation,
            withWatermark: false,
        }))
        .slice(0, displayVariations.length - leftoverVariationsCounter);

    const eventsEnrichment = {
        variationId: activeVariationId,
        variationPosition: activeVariationIndex + 1,
        taggedLogoStyles,
    };

    const handleSlideChange = (newIndex) => {
        setActiveVariationIndex(newIndex);
        onLogoProjectArrowClick(eventsEnrichment);
    };

    const handleLogoCardClick = () => {
        onLogoProjectCardClick(eventsEnrichment);
    };

    const onPreviewLogoClick = (newIndex) => {
        setActiveVariationIndex(newIndex);

        // Clicking on the preview logo should be reported as "Logo Maker - Listing - Variations Gallery Clicked" event
        onLogoProjectArrowClick({ ...eventsEnrichment, variationPosition: newIndex + 1 });
    };

    const onPreviewMouseEnter = (newIndex) => {
        setDisplayActiveVariationIndex(newIndex);
        onLogoProjectVariationHover({
            ...eventsEnrichment,
            variationId: variations[newIndex]?.id,
            variationPosition: newIndex + 1,
        });
    };

    const onPreviewMouseLeave = () => {
        setDisplayActiveVariationIndex(activeVariationIndex);
    };

    const handleLogoCardMouseEnter = () => {
        setShowPreviews(true);
        onLogoProjectHover(eventsEnrichment);
    };

    const MetadataCard = () => (
        <CardSellerProfile {...seller} {...(!isTablet && { variationsCounter: variations.length })} />
    );
    const PreviewCard = () => (
        <VariationsPreviewCard
            onClick={onPreviewLogoClick}
            onMouseEnter={onPreviewMouseEnter}
            onMouseLeave={onPreviewMouseLeave}
            onLoadMoreVariationsClick={onLoadMoreVariationsClick}
            activeIndex={activeVariationIndex}
            variationsURL={variationsUrl}
            maxPreviewsCounter={maxPreviewsCounter}
            activeVariationId={activeVariationId}
            moreCounter={leftoverVariationsCounter}
            variations={previewVariations}
            brandName={brandName}
            slogan={slogan}
            target={target}
        />
    );

    const footerProps = {
        isMobile,
        isTablet,
        withMetadataCard,
        showPreviews,
        metadataCardRender: MetadataCard,
        previewCardRender: PreviewCard,
    };

    setTriggerRef && setTriggerRef(containerRef);

    /* eslint-disable react/jsx-no-target-blank */
    return (
        <div
            className={cn('logo-project-card-wrapper', {
                'without-metadata': !withMetadataCard,
                'is-loading': isLoading,
            })}
            onMouseLeave={() => setShowPreviews(false)}
            onMouseEnter={handleLogoCardMouseEnter}
        >
            {isLoading ? (
                <div className="loading-state">
                    <div className="loading-card" />
                    <div className="loading-avatar">
                        <div className="loading-texts" />
                    </div>
                </div>
            ) : (
                <>
                    <a
                        className={cn('no-under-line', FAVORITE_PROJECT_CARD_CLASSNAME, className)}
                        ref={containerRef}
                        {...{ [PROJECT_CARD_DOM_ID_ATTRIBUTE]: id }}
                        href={variationsUrl}
                        target={target}
                        rel={target === TARGET_TYPES.BLANK ? 'noreferrer noopener' : 'noopener'}
                    >
                        {showUserSaved && (
                            <FavoriteButton
                                isFavorite={!!userSaved}
                                onChange={(isFavorite) => onUserSavingChange(id, activeVariationId, isFavorite)}
                            />
                        )}

                        {(isMobile || withArrowSlider) && (
                            <>
                                <DotsIndicator
                                    activeIndex={activeVariationIndex}
                                    total={displayVariations.length}
                                    direction={direction}
                                    setDirection={setDirection}
                                    onClick={handleLogoCardClick}
                                />
                                <Slider
                                    Component={Variation}
                                    isMobile={isMobile}
                                    setDirection={setDirection}
                                    slides={displayVariations.map((variation) => ({
                                        ...variation,
                                        dataVariationId: variation.id,
                                    }))}
                                    onSlideChange={handleSlideChange}
                                    onSlideClick={handleLogoCardClick}
                                    initialIndex={activeVariationIndex}
                                />
                            </>
                        )}
                        {!isMobile && !withArrowSlider && (
                            <Variation
                                {...displayVariations[displayActiveVariationIndex]}
                                onClick={handleLogoCardClick}
                            />
                        )}
                    </a>
                    <Footer {...footerProps} />
                </>
            )}
        </div>
    );
};

LogoProjectCard.propTypes = LOGO_PROJECT_CARD;

export default LogoProjectCard;
